import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Car, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      toast({
        title: "Hata",
        description: "Geçersiz şifre sıfırlama bağlantısı",
        variant: "destructive"
      });
      setIsVerifying(false);
    }
  }, [searchParams]);

  const verifyToken = async (tokenToVerify) => {
    try {
      const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
      
      const response = await fetch(`${BACKEND_URL}/api/password-reset/verify-token?token=${tokenToVerify}`);
      const data = await response.json();

      if (response.ok && data.valid) {
        setIsValidToken(true);
        setEmail(data.email);
      } else {
        toast({
          title: "Hata",
          description: data.detail || "Geçersiz veya süresi dolmuş bağlantı",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Token verification error:', error);
      toast({
        title: "Hata",
        description: "Bağlantı hatası. Lütfen tekrar deneyin.",
        variant: "destructive"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: "Hata",
        description: "Şifreler eşleşmiyor",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Hata",
        description: "Şifre en az 6 karakter olmalıdır",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
      
      const response = await fetch(`${BACKEND_URL}/api/password-reset/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Başarılı!",
          description: "Şifreniz başarıyla güncellendi. Giriş sayfasına yönlendiriliyorsunuz...",
        });
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast({
          title: "Hata",
          description: data.detail || "Şifre güncellenirken bir hata oluştu.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: "Hata",
        description: "Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardContent className="pt-6 text-center">
            <div className="text-white text-lg">Doğrulanıyor...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <div className="bg-red-500 p-4 rounded-2xl">
                <AlertCircle className="h-12 w-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-white text-2xl text-center font-display tracking-tight">Geçersiz Bağlantı</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-red-900/20 border-red-700">
              <AlertDescription className="text-red-400">
                Bu şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş. Lütfen yeni bir şifre sıfırlama talebi oluşturun.
              </AlertDescription>
            </Alert>
            <div className="text-center space-y-2">
              <Link to="/forgot-password">
                <Button className="w-full bg-gradient-to-r from-brand to-brand hover:from-brand hover:to-brand/90">
                  Yeni Şifre Sıfırlama Talebi
                </Button>
              </Link>
              <Link to="/login" className="block text-brand hover:text-brand text-sm font-semibold">
                ← Giriş sayfasına dön
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-brand to-brand p-4 rounded-2xl">
              <Car className="h-12 w-12 text-white" />
            </div>
          </div>
          <CardTitle className="text-white text-2xl text-center font-display tracking-tight">Yeni Şifre Oluştur</CardTitle>
          <CardDescription className="text-gray-400 text-center">
            {email && `${email} için yeni şifrenizi belirleyin`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-white">Yeni Şifre</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="En az 6 karakter"
                className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                required
                disabled={isLoading}
                minLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">Yeni Şifre (Tekrar)</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Şifrenizi tekrar girin"
                className="bg-gray-900 border-gray-700 text-white focus:border-brand"
                required
                disabled={isLoading}
                minLength={6}
              />
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <Alert className="bg-red-900/20 border-red-700">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <AlertDescription className="text-red-400 ml-2 text-sm">
                  Şifreler eşleşmiyor
                </AlertDescription>
              </Alert>
            )}

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-brand to-brand hover:from-brand hover:to-brand/90 text-white"
              disabled={isLoading || newPassword !== confirmPassword}
            >
              <Lock className="h-4 w-4 mr-2" />
              {isLoading ? 'Güncelleniyor...' : 'Şifremi Güncelle'}
            </Button>

            <div className="text-center">
              <Link to="/login" className="text-brand hover:text-brand text-sm font-semibold">
                ← Giriş sayfasına dön
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
